import * as React from 'react';
import { useHistory } from 'react-router';
import themePrimary from '../../styles/themePrimary';
import { Typography, makeStyles, useMediaQuery, Dialog, AppBar, IconButton, Toolbar } from '@material-ui/core';
import SmallButton, { SmallButtonProps } from '../common/Buttons/SmallButton';
import CloseIcon from '@material-ui/icons/Close';
import PhoneTextField, { PhoneTextFieldChangeHandler, PhoneTextFieldProps } from '../common/PhoneTextField';
import { TextFieldLabelPosition, TextFieldChangeHandler, TextFieldProps } from '../common/TextField';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { String_System_ErrorInvalidPhone, String_System_Done, String_TextReceiptDialog_Instruction, 
    String_TextReceiptDialog_Title} from '../../utilities/StringHelper';
import { ElementDimensions } from '../../styles/ElementDimensions';
import { Skin } from '../../models/common/Skin';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';

const useStyles = makeStyles({
    appBar: {
        position: 'relative',
        backgroundColor: props => (props as Skin).primary_main_color + ' !important',
    },
    buttonContainer: {
        padding: ElementDimensions.padding10 + 'px',
    },
    closeButton: {
        height: '30px',
        width: '30px',
        color: themePrimary.palette.primary.contrastText,
    },
    errorMessage: {
        fontSize: '1rem',
        color: themePrimary.palette.error.main
    },
    errorMessageContainer: {
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: ElementDimensions.margin10 + 'px',
        textAlign: 'center',
    },
    instructionText: {
        margin: ElementDimensions.margin10 + 'px',
        textAlign: 'center',
    },
    phoneContainer: {
        display: 'flex',
    },
    phoneField: {
        marginBottom: ElementDimensions.margin10 + 'px',
        textAlign: 'left',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'calc(100vw * .8)',
    },
    phoneFieldLandscape: {
        marginBottom: ElementDimensions.margin10 + 'px',
        textAlign: 'left',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'calc(100vw * .4)',
    },
    root: {

    },
    rootLandscape: {
        left: '25vw !important',
        width: '50vw',
    },
    title: {
        marginLeft: ElementDimensions.margin10 + 'px',
        flex: 1,
        color: themePrimary.palette.primary.contrastText,
    },
});

export type TextReceiptDialogCloseClickHandler = (event: React.MouseEvent<HTMLElement>) => any;
export type TextReceiptDialogDoneClickHandler = (event: React.MouseEvent<HTMLElement>, phoneNumber: string) => any;

export interface TextReceiptDialogProps {
    open: boolean;
    onDoneHandler: TextReceiptDialogDoneClickHandler;
    onCloseHandler: TextReceiptDialogCloseClickHandler;
}

const TextReceiptDialog = (props: TextReceiptDialogProps) => {
    const { open, onDoneHandler, onCloseHandler } = props;
    const isPortrait = true;
    let history = useHistory();
    const [isError, setIsError] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [phoneNumber, setPhoneNumber] = React.useState<string>('');
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const handleClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorMessage('');
        setIsError(false);
        setPhoneNumber('');
        onCloseHandler(event);
    };

    const handlePhoneChange: PhoneTextFieldChangeHandler = (newValue: string, country: string, phone: string) => {
        setPhoneNumber(newValue);
        setErrorMessage('');
        setIsError(false);
    };

    const phoneTextFieldProps: PhoneTextFieldProps = {
        country: null,
        label: '',
        type: null,
        text: phoneNumber,
        width: 160,
        disabled: false,
        labelPosition: TextFieldLabelPosition.Top,
        onChangeHandler: handlePhoneChange,
    };

    const handleOkButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!isValidPhoneNumber(phoneNumber)) {
            setIsError(true);
            setErrorMessage(String_System_ErrorInvalidPhone.find(s => s.key === userState.state.locale)!!.value);
        }
        else
        {
            onDoneHandler(event, phoneNumber);
        }
    }

    const buttonProps: SmallButtonProps = {
        disabled: false,
        alternateColor: false,
        label: String_System_Done.find(s => s.key === userState.state.locale)!!.value,
        onClickHandler: handleOkButtonClick,
    }

    return (
        <Dialog open={open} onClose={handleClose} disableBackdropClick={true} className={isPortrait ? classes.root : classes.rootLandscape}>
            <AppBar className={classes.appBar}>
                <Toolbar>
                    <IconButton  edge="start" color="inherit" onClick={handleClose} aria-label="close">
                        <CloseIcon className={classes.closeButton}/>
                    </IconButton>
                    <Typography variant="h6" className={classes.title}>
                        {String_TextReceiptDialog_Title.find(s => s.key === userState.state.locale)!!.value}
                    </Typography>
                </Toolbar>
            </AppBar>
            <Typography variant="h6" className={classes.instructionText}>
                {String_TextReceiptDialog_Instruction.find(s => s.key === userState.state.locale)!!.value}
            </Typography>
            <div className={classes.phoneContainer}>
                <div className={isPortrait ? classes.phoneField : classes.phoneFieldLandscape}>
                    <PhoneTextField 
                        country={phoneTextFieldProps.country}
                        label={phoneTextFieldProps.label}
                        text={phoneTextFieldProps.text}
                        type={phoneTextFieldProps.type}
                        onChangeHandler={phoneTextFieldProps.onChangeHandler}
                        width={phoneTextFieldProps.width}
                        disabled={phoneTextFieldProps.disabled}
                        labelPosition={phoneTextFieldProps.labelPosition} />
                    {isError &&
                        <div className={classes.errorMessageContainer}>
                            <Typography variant='body1' className={classes.errorMessage}>
                                {errorMessage}
                            </Typography>
                        </div>
                    }
                </div>
            </div>
            <div className={classes.buttonContainer}>
                <SmallButton 
                    alternateColor={buttonProps.alternateColor}
                    disabled={buttonProps.disabled}
                    label={buttonProps.label}
                    onClickHandler={buttonProps.onClickHandler}/>
            </div>
        </Dialog>
    );
};

export default TextReceiptDialog;